import React, { useCallback, useMemo } from 'react';
import { ClioDocumentTemplate } from '~/src/types/clioDocumentPicker';
import { getFormattedDateFromIsoString } from '~/src/utils/date';

interface DocumentTemplatePickerContentProps {
  templates: ClioDocumentTemplate[];
  selectedTemplates: ClioDocumentTemplate[];
  isLoading: boolean;
  error: string | null;
  onTemplateToggle: (template: ClioDocumentTemplate) => void;
  onSelectAll?: (templates: ClioDocumentTemplate[]) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClearSearch: () => void;
  isAtSelectionLimit?: boolean;
  totalCount: number;
  currentPage: number;
  itemsPerPage: number;
  _totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

export const DocumentTemplatePickerContent: React.FC<
  DocumentTemplatePickerContentProps
> = ({
  templates,
  selectedTemplates,
  isLoading,
  error,
  onTemplateToggle,
  onSelectAll,
  searchQuery,
  onSearchChange,
  onClearSearch,
  isAtSelectionLimit = false,
  totalCount,
  currentPage,
  itemsPerPage,
  _totalPages,
  onPreviousPage,
  onNextPage,
  onItemsPerPageChange,
  canGoPrevious,
  canGoNext,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Prepare template data for the table
  const templateData = useMemo(
    () =>
      templates.map((template) => ({
        id: `template-${template.id}`,
        type: 'template' as const,
        name: template.filename || 'Unknown Template',
        matter: '-', // Templates don't have matters
        fileSize: formatFileSize(template.size),
        lastEdited: template.updated_at
          ? getFormattedDateFromIsoString(template.updated_at)
          : '-',
        isSelected: selectedTemplates.some(
          (selected) => selected.id === template.id,
        ),
        originalItem: template,
      })),
    [templates, selectedTemplates],
  );

  const handleRowClick = useCallback(
    (rowData: any) => {
      if (rowData.type === 'template') {
        // Don't allow selection if at limit and template is not already selected
        if (isAtSelectionLimit && !rowData.isSelected) {
          return;
        }
        onTemplateToggle(rowData.originalItem);
      }
    },
    [onTemplateToggle, isAtSelectionLimit],
  );

  // Calculate header checkbox state
  const selectedSelectableTemplates = templates.filter((template) =>
    selectedTemplates.some((selected) => selected.id === template.id),
  );

  const allSelectableSelected =
    templates.length > 0 &&
    selectedSelectableTemplates.length === templates.length;
  const someSelectableSelected = selectedSelectableTemplates.length > 0;
  const isIndeterminate = someSelectableSelected && !allSelectableSelected;

  // Handle select all checkbox change
  const handleSelectAllChange = useCallback(() => {
    if (!onSelectAll) return;

    if (allSelectableSelected) {
      // Deselect all currently selected selectable templates
      onSelectAll(selectedSelectableTemplates);
    } else {
      // Select all selectable templates that are not currently selected
      const templatesToSelect = templates.filter(
        (template) =>
          !selectedTemplates.some((selected) => selected.id === template.id),
      );
      onSelectAll(templatesToSelect);
    }
  }, [
    onSelectAll,
    allSelectableSelected,
    selectedSelectableTemplates,
    templates,
    selectedTemplates,
  ]);

  // Memoized event handlers to prevent recreation on each render
  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange],
  );

  const handleSelectAllClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleSelectAllChange();
    },
    [handleSelectAllChange],
  );

  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleRowClickEvent = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const itemIndex = parseInt(
        target.getAttribute('data-item-index') || '0',
        10,
      );
      const item = templateData[itemIndex];
      if (item) {
        handleRowClick(item);
      }
    },
    [templateData, handleRowClick],
  );

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      const itemIndex = parseInt(
        target.getAttribute('data-item-index') || '0',
        10,
      );
      const item = templateData[itemIndex];
      if (item && item.type === 'template') {
        onTemplateToggle(item.originalItem);
      }
    },
    [templateData, onTemplateToggle],
  );

  const columns = [
    {
      label: '',
      labelRender: () => {
        const renderCheckboxContent = () => {
          if (allSelectableSelected || isIndeterminate) {
            // Show white minus icon when all are selected
            return (
              <svg
                className="h-3 w-3 text-white"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <rect x="2" y="7" width="12" height="2" rx="1" />
              </svg>
            );
          }
          // Empty for unchecked state
          return null;
        };

        return (
          <div className="flex flex-1 items-center justify-center">
            <button
              type="button"
              disabled={templates.length === 0 || !onSelectAll}
              onClick={handleSelectAllClick}
              className={`h-4 w-4 rounded-sm border flex items-center justify-center transition-colors ${
                allSelectableSelected || isIndeterminate
                  ? 'bg-blue-600 border-blue-600'
                  : 'border-gray-300 hover:border-gray-400'
              } ${
                templates.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
              title={
                templates.length === 0
                  ? 'No selectable templates'
                  : allSelectableSelected
                  ? 'Deselect all'
                  : 'Select all'
              }
            >
              {renderCheckboxContent()}
            </button>
          </div>
        );
      },
      dataKey: 'checkbox',
      className: 'w-[60px] min-w-[60px] max-w-[60px] flex-none',
      hideLabel: false,
      disableSort: true,
      render: (value: any, { rowData }: any, itemIndex: number) => (
        <div className="w-[60px] flex justify-center">
          <input
            type="checkbox"
            checked={rowData.isSelected}
            onChange={handleCheckboxChange}
            data-item-index={itemIndex}
            disabled={!rowData.isSelected && isAtSelectionLimit}
            className={`h-4 w-4 text-blue-600 rounded border-gray-300 ${
              !rowData.isSelected && isAtSelectionLimit
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={handleCheckboxClick}
          />
        </div>
      ),
    },
    {
      label: 'Name',
      dataKey: 'name',
      className: 'w-[300px] min-w-[300px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="flex items-center w-[300px]">
          <svg
            className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm text-gray-900 truncate">{rowData.name}</span>
        </div>
      ),
    },
    {
      label: 'Matter',
      dataKey: 'matter',
      className: 'w-[150px] min-w-[150px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="w-[150px]">
          <span className="text-sm text-gray-600 truncate block">
            {rowData.matter}
          </span>
        </div>
      ),
    },
    {
      label: 'File size',
      dataKey: 'fileSize',
      className: 'w-[100px] min-w-[100px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="w-[100px]">
          <span className="text-sm text-gray-600">{rowData.fileSize}</span>
        </div>
      ),
    },
    {
      label: 'Last edited at',
      dataKey: 'lastEdited',
      className: 'w-[120px] min-w-[120px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="w-[120px]">
          <span className="text-sm text-gray-600">{rowData.lastEdited}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="flex-1 flex flex-col bg-white min-h-0 h-full">
      {/* Search */}
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0 h-auto">
        <div className="flex items-center justify-end gap-4">
          {/* Search input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              className="w-64 pl-10 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Search templates"
            />
            {searchQuery && (
              <button
                onClick={onClearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded transition-colors"
                aria-label="Clear search"
              >
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-400 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Custom Table with Scrollable Body */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Table Header */}
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center h-12 px-4">
            {columns.map((column, index) => (
              <div
                key={`${column.label}-${index}`}
                className={`flex items-center text-xs font-semibold text-gray-700 capitalize tracking-wide ${
                  column.className || ''
                }`}
              >
                {column.hideLabel
                  ? null
                  : column.labelRender
                  ? column.labelRender()
                  : column.label}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Table Body */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading templates...</div>
            </div>
          ) : templateData.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">
                {searchQuery
                  ? 'No templates found matching your search'
                  : 'No document templates available'}
              </div>
            </div>
          ) : (
            <div>
              {templateData.map((item, itemIndex) => (
                <div
                  key={item.id}
                  data-item-index={itemIndex}
                  className={`flex items-center border-b border-gray-100 min-h-[48px] px-4 ${
                    !item.isSelected && isAtSelectionLimit
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                  onClick={handleRowClickEvent}
                >
                  {columns.map((column, columnIndex) => (
                    <div
                      key={columnIndex}
                      className={`flex items-center ${column.className || ''}`}
                    >
                      {column.render
                        ? column.render(
                            (item as any)[column.dataKey],
                            {
                              rowData: item,
                            },
                            itemIndex,
                          )
                        : (item as any)[column.dataKey]}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {/* Left side - Items info and per-page selector */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {totalCount > 0 ? (
                  <>
                    {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}
                    -{Math.min(currentPage * itemsPerPage, totalCount)}
                    of {totalCount} templates
                  </>
                ) : (
                  '0 templates'
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Right side - Pagination controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={onPreviousPage}
                disabled={!canGoPrevious || isLoading}
                className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={onNextPage}
                disabled={!canGoNext || isLoading}
                className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
